/**
 * Events for one client, styled to docs/prototype.html.
 *
 * The list is ordered by urgency rather than by date, which the server
 * computes: past deadline first, then deadline-near, then the rest, then
 * complete, then drafts. Archived events go into a separate collapsed section
 * that is hidden entirely when empty, so it self-drains as events age out.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { T } from '../lib/copyEdit.tsx';
import { t } from '../lib/copy';

interface Variation {
  id?: string;
  orientation: string;
  treatment: string;
}

interface EventItem {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'complete' | 'archived';
  startsOn: string | null;
  endsOn: string | null;
  timezone: string;
  deadline: {
    instant: string;
    date: string;
    time: string;
    display: string | null;
    daysUntil: number | null;
  } | null;
  notesForSponsors: string | null;
  graceWindowDays: number;
  counts?: { sponsors: number; approved: number; pendingReview: number; outstanding: number };
  variations: Variation[];
}

const STATUS_BADGE: Record<EventItem['status'], string> = {
  draft: 'b-neutral',
  active: 'b-info',
  complete: 'b-success',
  archived: 'b-neutral',
};

const STATUS_LABEL: Record<EventItem['status'], string> = {
  draft: 'Draft',
  active: 'Active',
  complete: 'Complete',
  archived: 'Archived',
};

/** The variations most events ask for, offered as one-click additions. */
const COMMON_VARIATIONS: Variation[] = [
  { orientation: 'Horizontal', treatment: 'Full color' },
  { orientation: 'Vertical / stacked', treatment: 'Full color' },
  { orientation: 'Horizontal', treatment: 'All-white / reversed' },
  { orientation: 'Icon / mark only', treatment: 'Full color' },
];

export default function Events() {
  const { clientId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [archived, setArchived] = useState<EventItem[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  /* `?new=1` opens the form straight away. It is how the dashboard's New Event
   * button gets here without a second copy of this form existing over there. */
  const [creating, setCreating] = useState(params.get('new') === '1');
  const [deleting, setDeleting] = useState<EventItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/clients/${clientId}/events`);
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as { events: EventItem[]; archived: EventItem[] };
      setEvents(body.events);
      setArchived(body.archived);
    } catch {
      setError('Could not load events.');
    }
  }, [clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Cleared once it has done its job, so a refresh or a back-navigation does
   * not reopen a form the user has already closed. */
  useEffect(() => {
    if (params.get('new') === '1') setParams({}, { replace: true });
  }, [params, setParams]);

  if (error) return <p className="auth-error">{error}</p>;
  if (!events) return <div className="app-loading" aria-busy="true" />;

  const formOpen = creating;

  return (
    <>
      <div className="phead">
        <div>
          <p className="eyebrow"><T k="common.appName" d="Sponsor Logos" /></p>
          <h1 className="title"><T k="events.title" d="Events" /></h1>
          <p className="lede"><T k="events.lede" d="Conferences and workshops collecting sponsor logos." /></p>
        </div>
        {!formOpen && (
          <button type="button" className="btn-compact" onClick={() => setCreating(true)}>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <T k="events.action.create" d="Create Event" />
          </button>
        )}
      </div>

      {deleting && (
        <DeleteEvent
          clientId={clientId}
          event={deleting}
          onCancel={() => setDeleting(null)}
          onDeleted={() => {
            setDeleting(null);
            void load();
          }}
        />
      )}

      {/* The create form replaces the list rather than stacking above it.
          Editing happens on the event's Setup tab, which is the single place
          an event's fields are changed. */}
      {formOpen ? (
        <EventForm
          clientId={clientId}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void load();
          }}
        />
      ) : events.length === 0 ? (
        <div className="empty">
          <p className="empty-title"><T k="events.empty.title" d="No events yet" /></p>
          <p className="empty-copy">
            <T
              k="events.empty.copy"
              d="Create an event to set your logo specs and start inviting sponsors."
            />
          </p>
        </div>
      ) : (
        <ul className="egrid">
          {events.map((e) => (
            <li key={e.id} className="ecard">
              {/* The whole card is the target, as in the prototype. The link
                  is stretched over the card rather than wrapping it, so the
                  accessible name stays the event title and no other content
                  ends up inside an anchor. */}
              <h3>
                <Link className="ecard-link" to={`/c/${clientId}/events/${e.id}`}>
                  {e.name}
                </Link>
              </h3>

              <div className="when">
                <svg className="icon icon-sm" viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                {e.startsOn ? formatRange(e.startsOn, e.endsOn) : 'Date to be confirmed'}
              </div>

              <div className="bar">
                <span style={{ width: `${progressPercent(e)}%` }} />
              </div>

              <div className="foot">
                <span className={`badge ${STATUS_BADGE[e.status]}`}>
                  <span className="d" />
                  {STATUS_LABEL[e.status]}
                </span>
                {deadlineFlag(e) ? (
                  <span className="flag-warn">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <path d="M12 9v4M12 17h.01" />
                    </svg>
                    {deadlineFlag(e)}
                  </span>
                ) : (
                  <span className="csub">{statusNote(e)}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Hidden entirely when empty, so it self-drains as events age out. */}
      {archived.length > 0 && (
        <>
          <button
            type="button"
            className={`arch-head${showArchived ? ' open' : ''}`}
            onClick={() => setShowArchived((v) => !v)}
          >
            <svg className="icon chev" viewBox="0 0 24 24" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
            Archived <span className="ct">· {archived.length}</span>
          </button>
          {showArchived && (
            <ul className="arch-list">
              {archived.map((e) => (
                <li key={e.id} className="arch-row">
                  <span className="badge b-neutral">
                    <span className="d" />
                    Archived
                  </span>
                  <b>{e.name}</b>
                  <span className="when">
                    {e.startsOn ? formatRange(e.startsOn, e.endsOn) : '—'}
                    {e.counts?.sponsors ? ` · ${e.counts.approved} of ${e.counts.sponsors}` : ''}
                  </span>
                  {/* Archived is a state, not a lock. Editing here is how an
                      event gets un-archived, so the actions have to be
                      reachable without restoring it first. */}
                  <div className="row-actions">
                    <Link className="cedit" to={`/c/${clientId}/events/${e.id}?tab=setup`}>
                      Edit
                    </Link>
                    <button type="button" className="cedit cedit--danger" onClick={() => setDeleting(e)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
}

/** "May 5 – 8, 2026", collapsing a same-month range the way the prototype does. */
function formatRange(startsOn: string, endsOn: string | null): string {
  const start = new Date(`${startsOn}T12:00:00Z`);
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric', timeZone: 'UTC' };

  if (!endsOn || endsOn === startsOn) {
    return start.toLocaleDateString('en-US', { ...opts, year: 'numeric' });
  }

  const end = new Date(`${endsOn}T12:00:00Z`);
  const sameMonth = startsOn.slice(0, 7) === endsOn.slice(0, 7);

  return sameMonth
    ? `${start.toLocaleDateString('en-US', opts)} – ${end.getUTCDate()}, ${end.getUTCFullYear()}`
    : `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

/** Approved sponsors as a percentage. Zero sponsors reads as 0, not NaN. */
function progressPercent(e: EventItem): number {
  const total = e.counts?.sponsors ?? 0;
  if (total === 0) return 0;
  return Math.round(((e.counts?.approved ?? 0) / total) * 100);
}

/**
 * The amber deadline flag, or null when there is nothing urgent to say.
 *
 * Only shown while an event is actually collecting. A draft has not been
 * launched and a complete event is done, so flagging their deadlines would be
 * noise on rows nobody needs to act on.
 */
function deadlineFlag(e: EventItem): string | null {
  if (e.status !== 'active') return null;
  const days = e.deadline?.daysUntil;
  if (typeof days !== 'number') return null;
  if (days < 0) return `Deadline passed ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'Deadline today';
  if (days <= 14) return `Deadline ${days} day${days === 1 ? '' : 's'} away`;
  return null;
}

/** The quieter line shown when there is no deadline warning. */
function statusNote(e: EventItem): string {
  if (e.status === 'draft') return 'Not launched';
  if (e.status === 'complete') return 'Complete';
  const total = e.counts?.sponsors ?? 0;
  if (total === 0) return 'No sponsors yet';
  return `${e.counts?.approved ?? 0} of ${total} approved`;
}

/** Urgency in words. Past deadline reads as overdue rather than "-3 days". */
function describeDays(days: number): string {
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} past deadline`;
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `${days} days away`;
}

/**
 * Mirrors the server's cross-field rules so the form can complain immediately
 * rather than after a round trip.
 *
 * The server remains authoritative and runs the same checks against the MERGED
 * record — this copy exists purely for responsiveness, and being out of step
 * would only ever mean a slower message, never a bad save.
 */
function checkDates(startsOn: string, endsOn: string, deadlineDate: string): FieldErrors {
  const problems: FieldErrors = {};
  if (startsOn && endsOn && endsOn < startsOn) {
    problems.endsOn = 'The end date cannot be before the start date.';
  }
  if (startsOn && deadlineDate && deadlineDate >= startsOn) {
    problems.deadlineDate = 'The logo deadline must be before the event starts.';
  }
  return problems;
}

type FieldErrors = Partial<Record<'endsOn' | 'deadlineDate', string>>;

/**
 * Creating an event. Editing lives on the event's Setup tab, which carries the
 * full specification — keeping both here would mean two forms to change the
 * same fields, which is how they drift apart.
 */
function EventForm({
  clientId,
  onCancel,
  onSaved,
}: {
  clientId: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [status, setStatus] = useState<EventItem['status']>('draft');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('17:00');
  const [notes, setNotes] = useState('');
  const [grace, setGrace] = useState('2');

  const [variations, setVariations] = useState<Variation[]>(COMMON_VARIATIONS.slice(0, 2));

  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [fields, setFields] = useState<FieldErrors>({});

  // Recomputed as the user types, so a bad pair is flagged the moment it
  // exists rather than on submit.
  const liveErrors = checkDates(startsOn, endsOn, deadlineDate);
  const shown: FieldErrors = { ...liveErrors, ...fields };
  const blocked = Object.keys(liveErrors).length > 0;

  function addVariation(v: Variation) {
    const exists = variations.some(
      (x) => x.orientation === v.orientation && x.treatment === v.treatment,
    );
    if (!exists) setVariations((prev) => [...prev, v]);
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const problems = checkDates(startsOn, endsOn, deadlineDate);
    if (Object.keys(problems).length > 0) {
      setFields(problems);
      return;
    }

    setSaving(true);
    setProblem(null);
    setFields({});

    const payload = {
      name: name.trim(),
      status,
      startsOn: startsOn || null,
      endsOn: endsOn || null,
      deadlineDate: deadlineDate || null,
      deadlineTime,
      notesForSponsors: notes.trim() || null,
      graceWindowDays: Number(grace),
      variations,
    };

    const res = await fetch(`/api/clients/${clientId}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    setSaving(false);
    if (res.ok) {
      onSaved();
      return;
    }

    const body = (await res.json().catch(() => ({}))) as { error?: string; fields?: FieldErrors };
    if (body.fields) setFields(body.fields);
    setProblem(body.error ?? 'That did not save. Check the fields and try again.');
  }

  return (
    <form className="panel" onSubmit={submit}>
      <div className="panel-head">
        <h3><T k="events.form.title" d="New Event" /></h3>
        <span className="hint">deadlines and reminders run on Eastern Time</span>
      </div>

      <div className="panel-body">
        <div className="fg">
          <label className="fl" htmlFor="name"><T k="events.form.name" d="Event Name" /></label>
          <input
            id="name"
            required
            maxLength={200}
            className="fi"
            placeholder={t('events.form.namePlaceholder', '2026 Annual Conference')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="startsOn"><T k="events.form.startsOn" d="Event Starts" /></label>
            <input
              id="startsOn"
              type="date"
              className="fi"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </div>
          <div className="fg">
            <label className="fl" htmlFor="endsOn"><T k="events.form.endsOn" d="Event Ends" /></label>
            <input
              id="endsOn"
              type="date"
              className={`fi${shown.endsOn ? ' fi--bad' : ''}`}
              value={endsOn}
              // The browser blocks earlier dates outright where it can, which
              // is faster than any message. The check still runs, because this
              // attribute is trivially removed.
              min={startsOn || undefined}
              aria-invalid={shown.endsOn ? true : undefined}
              onChange={(e) => setEndsOn(e.target.value)}
            />
            {shown.endsOn && <p className="ferror">{shown.endsOn}</p>}
          </div>
        </div>

        {/* The conference dates, not the upload window — an easy misread with
            a field called Logo Deadline directly beneath them. */}
        <p className="fdesc" style={{ marginTop: -4 }}>
          The conference dates. When sponsors can upload is set by the logo deadline below.
        </p>

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="deadlineDate"><T k="events.form.deadline" d="Logo Deadline" /></label>
            <div className="fetch-row">
              <input
                id="deadlineDate"
                type="date"
                className={`fi${shown.deadlineDate ? ' fi--bad' : ''}`}
                value={deadlineDate}
                max={startsOn ? previousDay(startsOn) : undefined}
                aria-invalid={shown.deadlineDate ? true : undefined}
                onChange={(e) => setDeadlineDate(e.target.value)}
              />
              <input
                type="time"
                className="fi"
                style={{ maxWidth: 120 }}
                aria-label="Deadline time"
                value={deadlineTime}
                onChange={(e) => setDeadlineTime(e.target.value)}
              />
            </div>
            {shown.deadlineDate ? (
              <p className="ferror">{shown.deadlineDate}</p>
            ) : (
              <p className="fdesc"><T k="events.form.deadlineHint" d="Eastern Time. Reminders are anchored to this." /></p>
            )}
          </div>

          <div className="fg">
            <label className="fl" htmlFor="status"><T k="events.form.status" d="Status" /></label>
            <select
              id="status"
              className="fi"
              value={status}
              onChange={(e) => setStatus(e.target.value as EventItem['status'])}
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="complete">Complete</option>
            </select>
            <p className="fdesc"><T k="events.form.statusHint" d="Draft means sponsors cannot see it yet." /></p>
          </div>
        </div>

        <div className="fg">
          <label className="fl"><T k="events.form.versions" d="Versions Requested" /></label>
          <p className="fdesc" style={{ marginTop: 0, marginBottom: 10 }}>
            What each sponsor is asked for. Drives their upload checklist and the review screen.
          </p>

          {variations.length > 0 && (
            <ul className="vlist">
              {variations.map((v, i) => (
                <li key={`${v.orientation}-${v.treatment}`} className="vrow">
                  <span>
                    <b>{v.orientation}</b> · {v.treatment}
                  </span>
                  <button
                    type="button"
                    className="vremove"
                    aria-label={`Remove ${v.orientation} ${v.treatment}`}
                    onClick={() => setVariations((prev) => prev.filter((_, x) => x !== i))}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="vadd">
            {COMMON_VARIATIONS.filter(
              (c) => !variations.some((v) => v.orientation === c.orientation && v.treatment === c.treatment),
            ).map((c) => (
              <button
                key={`${c.orientation}-${c.treatment}`}
                type="button"
                className="vchip"
                onClick={() => addVariation(c)}
              >
                + {c.orientation} · {c.treatment}
              </button>
            ))}
          </div>
        </div>

        <div className="frow2">
          <div className="fg">
            <label className="fl" htmlFor="grace"><T k="events.form.grace" d="Grace Window After Deadline" /></label>
            <select id="grace" className="fi" value={grace} onChange={(e) => setGrace(e.target.value)}>
              <option value="0">None</option>
              <option value="2">2 days</option>
              <option value="5">5 days</option>
              <option value="10">10 days</option>
              <option value="14">14 days</option>
            </select>
            <p className="fdesc">How long a sponsor's upload link keeps working.</p>
          </div>
          <div className="fg">
            <label className="fl" htmlFor="notes"><T k="events.form.notes" d="Notes For Sponsors" /></label>
            <textarea
              id="notes"
              className="fi ft"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-compact" disabled={saving || blocked}>
            {saving ? 'Saving…' : 'Create Event'}
          </button>
        </div>
      </div>
    </form>
  );
}

/** The day before a YYYY-MM-DD date, for the deadline picker's max. */
function previousDay(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

interface DeleteImpact {
  sponsors: number;
  submissions: number;
  emails: number;
}

/**
 * Deleting an event.
 *
 * The first attempt asks the server what would be destroyed. If the answer is
 * "nothing", it is already gone — a confirmation dialog for an empty event
 * teaches people to click through the ones that matter. If there is history,
 * the server refuses and returns the counts, and the name has to be typed to
 * proceed.
 */
function DeleteEvent({
  clientId,
  event,
  onCancel,
  onDeleted,
}: {
  clientId: string;
  event: EventItem;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const [impact, setImpact] = useState<DeleteImpact | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [asked, setAsked] = useState(false);

  const url = `/api/clients/${clientId}/events/${event.id}`;

  async function attempt(confirmName?: string) {
    setBusy(true);
    setProblem(null);

    const res = await fetch(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(confirmName ? { confirmName } : {}),
    });

    setBusy(false);

    if (res.status === 204) {
      onDeleted();
      return;
    }

    if (res.status === 409) {
      const body = (await res.json()) as { impact: DeleteImpact };
      setImpact(body.impact);
      setAsked(true);
      return;
    }

    setProblem('That could not be deleted.');
  }

  // Ask once on mount: an empty event should not require a ceremony.
  useEffect(() => {
    if (!asked) void attempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!asked) return <div className="app-loading" aria-busy="true" />;

  const parts = [
    impact?.sponsors ? `${impact.sponsors} sponsor${impact.sponsors === 1 ? '' : 's'}` : null,
    impact?.submissions
      ? `${impact.submissions} logo submission${impact.submissions === 1 ? '' : 's'}`
      : null,
    impact?.emails ? `${impact.emails} sent email${impact.emails === 1 ? '' : 's'}` : null,
  ].filter(Boolean);

  return (
    <div className="panel panel--danger">
      <div className="panel-head panel-head--danger">
        <h3>Delete {event.name}</h3>
      </div>
      <div className="panel-body">
        <p className="fdesc" style={{ marginTop: 0 }}>
          This permanently removes {parts.join(', ')} along with the event. Approved logo files stay
          in the library, because they belong to the sponsor rather than the event, but the record
          of who was asked, what they sent and who approved it does not survive.
        </p>
        <p className="fdesc">
          If the event is simply finished, archive it instead. Archived events stay searchable and
          can be reopened.
        </p>

        <div className="fg">
          <label className="fl" htmlFor="confirmName">
            Type <b>{event.name}</b> to confirm
          </label>
          <input
            id="confirmName"
            className="fi"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
        </div>

        {problem && <p className="auth-error">{problem}</p>}

        <div className="factions">
          <button type="button" className="btn-compact btn-compact--quiet" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-compact btn-compact--danger"
            disabled={busy || typed !== event.name}
            onClick={() => void attempt(typed)}
          >
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}
